/**
 * useSnackbar Composable
 *
 * Provides a clean interface for showing snackbar notifications.
 * Delegates to Pinia useAppStore snackbar actions.
 *
 * @version 3.0.0
 * @migrated Vuex store.dispatch('app/showSnackbar*') → useAppStore() Pinia
 */

import { useAppStore } from '@/stores/app'

export type SnackbarType = 'success' | 'error' | 'info'

export interface UseSnackbarReturn {
  /** Show a success snackbar */
  showSuccess: (message: string) => void
  /** Show an error snackbar */
  showError: (message: string) => void
  /** Show an info snackbar */
  showInfo: (message: string) => void
  /** Show a snackbar with specified type */
  show: (message: string, type: SnackbarType) => void
}

/**
 * Composable for managing snackbar notifications
 *
 * @example
 * ```ts
 * const { showSuccess, showError, showInfo } = useSnackbar()
 *
 * showSuccess('Operation completed!')
 * showError('Something went wrong')
 * showInfo('Did you know?')
 * ```
 */
export function useSnackbar(): UseSnackbarReturn {
  const appStore = useAppStore()

  const showSuccess = (message: string): void => {
    appStore.showSuccess(message)
  }

  const showError = (message: string): void => {
    appStore.showError(message)
  }

  const showInfo = (message: string): void => {
    appStore.showInfo(message)
  }

  const show = (message: string, type: SnackbarType): void => {
    switch (type) {
      case 'success':
        showSuccess(message)
        break
      case 'error':
        showError(message)
        break
      case 'info':
        showInfo(message)
        break
    }
  }

  return {
    showSuccess,
    showError,
    showInfo,
    show,
  }
}

export default useSnackbar
